import React from 'react';
import { px2rem } from '@utils/px2rem';

interface CommentDecoration {
    bubble?: {
        startColor?: string;
        endColor?: string;
    };
    pendant?: {
        image?: string;
        imageWidth?: number;
        imageHeight?: number;
    };
}

interface CommentBubbleProps {
    decoration?: CommentDecoration | null;
    platform: 'pc' | 'h5';
    children: React.ReactNode;
}

// 接口下发的是 Android 常用的 #AARRGGBB，浏览器使用前需要转换。
const androidColorToRgba = (color?: string) => {
    if (!color) return 'transparent';

    const normalized = color.trim();
    const match = normalized.match(/^#([0-9a-fA-F]{8})$/);
    if (!match) return normalized;

    const value = match[1];
    const alpha = parseInt(value.slice(0, 2), 16) / 255;
    const red = parseInt(value.slice(2, 4), 16);
    const green = parseInt(value.slice(4, 6), 16);
    const blue = parseInt(value.slice(6, 8), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const CommentBubble: React.FC<CommentBubbleProps> = ({ decoration, platform, children }) => {
    const bubble = decoration?.bubble;
    const pendant = decoration?.pendant;
    const pendantImage = pendant?.image;

    if (!bubble && !pendantImage) return <>{children}</>;

    const isH5 = platform === 'h5';
    const pendantWidthPx = Number(pendant?.imageWidth) > 0 ? Number(pendant?.imageWidth) : undefined;
    const pendantHeightPx = Number(pendant?.imageHeight) > 0 ? Number(pendant?.imageHeight) : undefined;
    const leftHorizontalPaddingPx = isH5 ? 16 : 24;
    const rightHorizontalPaddingPx = isH5 ? 32 : 24;
    const contentToPendantGapPx = isH5 ? 16 : 14;
    const pendantExposePx = pendantWidthPx
        ? Math.max(0, pendantWidthPx - (rightHorizontalPaddingPx - contentToPendantGapPx))
        : 0;
    const pendantLeftShiftPx = isH5 ? 8 : 4;
    const pendantWidth = pendantWidthPx
        ? (isH5 ? px2rem(pendantWidthPx) : `${pendantWidthPx}px`)
        : undefined;
    const pendantHeight = pendantHeightPx
        ? (isH5 ? px2rem(pendantHeightPx) : `${pendantHeightPx}px`)
        : undefined;
    const pendantSpace = isH5 ? px2rem(pendantExposePx) : `${pendantExposePx}px`;
    // 右内边距按设计稿固定：H5 32px，PC 24px。
    const paddingRight = isH5
        ? px2rem(rightHorizontalPaddingPx)
        : `${rightHorizontalPaddingPx}px`;
    const paddingLeft = isH5
        ? px2rem(leftHorizontalPaddingPx)
        : `${leftHorizontalPaddingPx}px`;

    const wrapperStyle: React.CSSProperties = {
        boxSizing: 'border-box',
        display: 'table',
        position: 'relative',
        maxWidth: pendantImage ? `calc(100% - ${pendantSpace})` : '100%',
        padding: isH5
            ? `${px2rem(4)} ${paddingRight} ${px2rem(4)} ${paddingLeft}`
            : `6px ${paddingRight} 6px 4px`,
        // 四个角圆角保持一致，挂件仅叠加展示在气泡外侧，不裁切气泡本身的圆角
        borderRadius: isH5
            ? px2rem(32)
            : '16px',
        background: bubble
            ? `linear-gradient(90deg, ${androidColorToRgba(bubble.startColor)}, ${androidColorToRgba(bubble.endColor)})`
            : undefined,
    };

    // 根据挂件宽度反推外露距离，同时满足：
    // 1. 内容到背景右边固定留白
    // 2. 内容到挂件左边保持 14px
    const pendantOffsetRight = isH5
        ? px2rem(Math.max(0, pendantExposePx - pendantLeftShiftPx))
        : `${Math.max(0, pendantExposePx - pendantLeftShiftPx)}px`;

    const pendantStyle: React.CSSProperties = {
        display: 'block',
        position: 'absolute',
        zIndex: 1,
        right: `-${pendantOffsetRight}`,
        bottom: 0,
        width: pendantWidth,
        height: pendantHeight,
        objectFit: 'contain',
        pointerEvents: 'none',
    };

    return (
        <div style={wrapperStyle}>
            {children}
            {pendantImage && <img src={pendantImage} alt="" aria-hidden="true" style={pendantStyle} />}
        </div>
    );
};

export default CommentBubble;
